/**
 * Application chrome: the bar at the top, the ticker under it, the rails
 * beside it. Three topbar variants exist and no screen invents a fourth.
 */

import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { formatTime } from '@metal/shared'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'

export function AppShell({
  topbar,
  appbar,
  ticker,
  rail,
  railOpen,
  onRailClose,
  operator,
  children,
  bottomBar,
}: {
  topbar: ReactNode
  /** The compact top bar shown below the lg breakpoint, where the wide
      desktop topbar cannot fit. Hidden by CSS on the desktop, exactly as the
      desktop topbar is hidden on it, so both can sit in the DOM at once. */
  appbar?: ReactNode
  ticker?: ReactNode
  rail?: ReactNode
  /** Below the lg breakpoint the `rail` is an off-canvas drawer rather than a
      fixed column. This holds it open, and the appbar's menu button drives it. */
  railOpen?: boolean
  onRailClose?: () => void
  operator?: boolean
  bottomBar?: ReactNode
  children: ReactNode
}) {
  return (
    <div className={cx('app', operator && 'op', railOpen && 'rail-open')}>
      <a className="skip-link" href="#main">
        پرش به محتوا
      </a>
      {appbar}
      {topbar}
      {ticker}
      <div className="app-body">
        {rail}
        {rail && (
          <button type="button" className="rail-scrim" aria-label="بستن منو" onClick={onRailClose} />
        )}
        <main className="main" id="main">
          {children}
        </main>
      </div>
      {bottomBar}
    </div>
  )
}

export function Brand({
  name = 'فیدار تک',
  sub,
  to = '/',
}: {
  name?: string
  sub: string
  to?: string
}) {
  return (
    <NavLink className="brand" to={to} style={{ color: 'inherit' }}>
      <span className="brand-mark">
        <Icon name="brand-mark" />
      </span>
      <span>
        <span className="brand-name" style={{ display: 'block' }}>
          {name}
        </span>
        <span className="brand-sub">{sub}</span>
      </span>
    </NavLink>
  )
}

export function Topbar({
  variant = 'app',
  children,
  className,
}: {
  variant?: 'app' | 'public' | 'operator'
  children: ReactNode
  className?: string
}) {
  return (
    <header className={cx('topbar', className)} data-variant={variant}>
      {children}
    </header>
  )
}

/**
 * The compact top bar for the phone and tablet band, where the wide desktop
 * topbar with its five nav rows cannot fit. It carries only what a bar this
 * narrow must: the brand, the market session, and the two account controls.
 * Navigation lives in the bottom tab bar down here, not in the top bar. Like
 * `Topbar`, it is a dumb wrapper the layout fills, so each layout keeps its
 * own idea of what belongs in it.
 */
export function AppBar({ children, className }: { children: ReactNode; className?: string }) {
  return <header className={cx('appbar', className)}>{children}</header>
}

export interface NavItemSpec {
  to: string
  label: string
  icon: IconName
  /** Extra paths that keep this row lit. */
  match?: string[]
}

export function Nav({ items }: { items: NavItemSpec[] }) {
  return (
    <nav className="nav">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            cx(isActive || matchesAny(it.match) ? 'is-on' : undefined)
          }
        >
          <Icon name={it.icon} />
          {it.label}
        </NavLink>
      ))}
    </nav>
  )
}

function matchesAny(patterns?: string[]): boolean {
  if (!patterns?.length) return false
  const path = window.location.pathname
  return patterns.some((p) =>
    p.endsWith('*') ? path.startsWith(p.slice(0, -1)) : path === p,
  )
}

/**
 * The market session pill.
 *
 * Open reads «بازار باز تا ساعت ۱۷:۰۰», closed reads
 * «بازار بسته، بازگشایی ساعت ۰۹:۰۰». The times come from the session window;
 * they are never literals.
 */
export function SessionClock({
  open,
  closesAt,
  opensAt,
  short,
  className,
}: {
  open: boolean
  closesAt: Date | null
  opensAt: Date | null
  /** The operator bar uses the short form. */
  short?: boolean
  className?: string
}) {
  if (open) {
    const t = closesAt ? formatTime(closesAt) : '۱۷:۰۰'
    return (
      <span className={cx('session', className)}>
        <span className="dot dot-live pulse" />
        {short ? (
          `بازار باز تا ${t}`
        ) : (
          <span>
            بازار باز تا <span className="session-word">ساعت </span>
            {t}
          </span>
        )}
      </span>
    )
  }
  const t = opensAt ? formatTime(opensAt) : '۰۹:۰۰'
  return (
    <span className={cx('session is-closed', className)}>
      <Icon name="clock" size="sm" />
      {short ? (
        `بازار بسته تا ${t}`
      ) : (
        <span>
          بازار بسته<span className="session-word">، بازگشایی ساعت</span>
          <span className="session-short"> تا</span> {t}
        </span>
      )}
    </span>
  )
}

/**
 * One ticker tile, exactly as the API publishes it.
 *
 * Every visible string is already Persian and already formatted by the
 * server, so this component formats nothing. That is deliberate: the ticker
 * is the one strip where a client side rounding difference would put two
 * different prices in front of two users looking at the same market.
 */
export interface TickerItem {
  key: string
  labelFa: string
  /** Null when the series has no figure for the day. */
  valueFa: string | null
  unitFa?: string
  /** Signed percent, pre-rendered. Empty when the series has no comparison. */
  changeFa?: string
  changePercent?: number | null
  /**
   * The freshness code the API sends with every tile (LIVE, FRESH,
   * PREVIOUS_DAY, STALE, UNAVAILABLE, CORRECTED, ARCHIVED). Only an
   * out-of-date figure changes how the tile reads.
   */
  badge?: string
  /** The freshness in words. Shown only as the tooltip of a stale figure. */
  badgeFa?: string
  /** True only for the platform's own matched price. */
  live?: boolean
}

/** Freshness codes whose figure is drawn muted, with the reason on hover. */
const STALE_BADGES = new Set(['STALE', 'UNAVAILABLE', 'ARCHIVED'])

/**
 * The market strip.
 *
 * External feeds are T-1 by contract. That is said once, by the strip's
 * stamp when the layout passes one, never as a chip on every tile. A stale
 * figure is drawn muted with the reason as its tooltip. Only the platform's
 * own matched price is live, and it is the only item that gets the live dot.
 * Nothing here polls: the strip renders a daily snapshot.
 */
export function MarketTicker({
  items,
  leadFa = 'مظنه بازار',
  stampFa,
}: {
  items: TickerItem[]
  leadFa?: string
  stampFa?: string | null
}) {
  const feeds = items.filter((i) => !i.live)
  const live = items.filter((i) => i.live)

  return (
    <div className="ticker">
      <span className="tk-lead">
        <Icon name="chart" style={{ inlineSize: 14, blockSize: 14 }} />
        {leadFa}
      </span>
      {feeds.map((it) => {
        const stale = it.badge !== undefined && STALE_BADGES.has(it.badge)
        const missing = !it.valueFa?.trim()
        return (
          <span
            className="tk-item"
            key={it.key}
            title={stale && it.badgeFa ? it.badgeFa : undefined}
          >
            <span className="tk-n">{it.labelFa}</span>
            {/* A stale figure keeps the value's size and weight and takes the
                label's muted tone (the token .tk-n already uses), not a chip.
                A missing one is the same muted «-». */}
            <span
              className="tk-v"
              style={stale || missing ? { color: 'var(--p-ink-200)' } : undefined}
            >
              {missing ? '-' : it.valueFa}
            </span>
            {it.changeFa && !stale && (
              <span className={cx('tk-d', (it.changePercent ?? 0) >= 0 ? 'up' : 'down')}>
                {it.changeFa}
              </span>
            )}
          </span>
        )
      })}
      {live.length > 0 && <span className="tk-sep" />}
      {live.map((it) => (
        <span className="tk-item" key={it.key}>
          <span className="tk-live" />
          <span className="tk-n">{it.labelFa}</span>
          <span className="tk-v" style={it.valueFa?.trim() ? undefined : { color: 'var(--p-ink-200)' }}>
            {it.valueFa?.trim() ? it.valueFa : '-'}
          </span>
        </span>
      ))}
      {stampFa && <span className="tk-stamp">{stampFa}</span>}
    </div>
  )
}

export function PageHeader({
  title,
  meta,
  actions,
  align = 'start',
  className,
}: {
  title: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  align?: 'start' | 'end'
  className?: string
}) {
  return (
    <div className={cx('page-h', align === 'end' && 'page-h-end', className)}>
      <div>
        {typeof title === 'string' ? <h1 className="t-h2">{title}</h1> : title}
        {meta && <div className="row" style={{ gap: 8, marginBlockStart: 8 }}>{meta}</div>}
      </div>
      {actions && <div className="row" style={{ gap: 10 }}>{actions}</div>}
    </div>
  )
}

export function Page({
  narrow,
  wide,
  className,
  children,
  ...rest
}: {
  narrow?: boolean
  wide?: boolean
  className?: string
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('page', narrow && 'page-narrow', wide && 'page-wide', className)} {...rest}>
      {children}
    </div>
  )
}

/* ==========================================================================
   Rails
   ========================================================================== */

export interface RailRow {
  key: string
  to: string
  label: string
  icon: IconName
  /** Items waiting in this section. Hidden at zero. */
  count?: number
  /** Muted and inert. */
  disabled?: boolean
  /** Muted with a lock, and still a link: its page says what is missing. */
  locked?: boolean
}

export interface RailGroup {
  heading?: string
  rows: RailRow[]
}

export function Rail({
  groups,
  activeKey,
  className,
}: {
  groups: RailGroup[]
  activeKey?: string
  className?: string
}) {
  return (
    <aside className={cx('side', className)}>
      {groups.map((g, gi) => (
        <div key={g.heading ?? `g-${gi}`} style={{ display: 'contents' }}>
          {g.heading && <div className="side-group">{g.heading}</div>}
          {g.rows.map((r) =>
            r.locked ? (
              // Muted, with a lock, and still a way in: the page it opens
              // says which access is missing and which roles carry it.
              <NavLink key={r.key} to={r.to} className="side-row is-locked">
                <Icon name={r.icon} />
                {r.label}
                <Icon name="lock" size="sm" className="side-lock" />
              </NavLink>
            ) : r.disabled ? (
              <span key={r.key} className="side-row" aria-disabled="true">
                <Icon name={r.icon} />
                {r.label}
                <RailCount count={r.count} />
              </span>
            ) : (
              <NavLink
                key={r.key}
                to={r.to}
                className={activeKey === r.key ? 'is-on' : undefined}
              >
                <Icon name={r.icon} />
                {r.label}
                <RailCount count={r.count} />
              </NavLink>
            ),
          )}
        </div>
      ))}
    </aside>
  )
}

/**
 * A rail count is drawn only when there is something in it: never a zero.
 * Keyed by its value, so a new count lands with a small pop.
 */
function RailCount({ count }: { count?: number }) {
  if (count === undefined || count <= 0) return null
  return (
    <span key={count} className="side-count">
      {fa(count)}
    </span>
  )
}

function fa(n: number): string {
  return String(n).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
}
